import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Linkedin, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import { TEAM } from "@/data/content";

const QUESTIONS = ["What should we cook?", "What should we wear?", "What should we buy?", "How do we manage our home?"];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32">
      <section className="relative overflow-hidden py-12 md:py-20">
        <div className="mesh-bg">
          <div className="mesh-blob" style={{ width: 460, height: 460, background: "#F28C82", top: -100, left: -60 }} />
          <div className="mesh-blob" style={{ width: 400, height: 400, background: "#B8E3D9", bottom: -80, right: -40 }} />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">About</p>
            <h1 className="mt-6 font-heading text-4xl font-light tracking-tighter text-[#4B2E5A] md:text-6xl">Our mission</h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-[#4B2E5A]/70">
              Today's women — and increasingly entire households — make hundreds of small decisions every day. We're building AI-powered products that remove this decision fatigue.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-8">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal className="glass rounded-[24px] p-8 md:p-12">
            <div className="grid gap-4 sm:grid-cols-2">
              {QUESTIONS.map((q) => (
                <div key={q} className="flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-[#B8E3D9]" />
                  <span className="text-lg text-[#4B2E5A]/80">{q}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Vision teaser */}
      <section className="py-12 md:py-20">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-12">
          <Reveal>
            <h2 className="font-heading text-3xl font-light tracking-tighter text-[#4B2E5A] md:text-5xl">We're not building apps.</h2>
            <h2 className="mt-2 font-heading text-3xl font-light tracking-tighter text-[#F28C82] md:text-5xl">
              We're building the operating system for everyday life.
            </h2>
            <Link to="/vision" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#4B2E5A] hover:text-[#F28C82]">
              Read our full vision <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Team */}
      <section data-testid="about-founder" className="py-12 md:py-20">
        <div className="mx-auto max-w-6xl space-y-6 px-6 md:space-y-8 md:px-12">
          {TEAM.map((person, idx) => (
            <div key={person.name} className="glass grid items-center gap-10 rounded-[24px] p-8 md:grid-cols-2 md:p-14">
              <Reveal className={idx % 2 ? "md:order-2" : ""}>
                <div className="relative mx-auto h-72 w-72 overflow-hidden rounded-[24px] shadow-[0_20px_60px_rgba(75,46,90,0.15)]">
                  <img src={person.photo} alt={person.label} className="h-full w-full object-cover" />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">{person.label}</p>
                <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-[#4B2E5A]">{person.name}</h2>
                <div className="mt-6 space-y-3">
                  {person.bio.map((line) => (
                    <div key={line} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-[#B8E3D9]" />
                      <span className="text-lg text-[#4B2E5A]/75">{line}</span>
                    </div>
                  ))}
                </div>
                <a href={person.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`about-${idx === 0 ? "founder" : "cofounder"}-linkedin`} className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#4B2E5A]/10 bg-white/70 px-6 py-3 text-sm font-medium text-[#4B2E5A] hover:bg-white">
                  <Linkedin className="h-4 w-4" strokeWidth={1.5} /> Connect on LinkedIn
                </a>
              </Reveal>
            </div>
          ))}
        </div>
      </section>
      <Newsletter />
    </div>
  );
}
